import { Core } from "@faire/design-tokens";
import { READ_MORE } from "@faire/help-center/consts";
import { useSectionStore } from "@faire/help-center/hooks/useSectionStore";
import { NAVIGATION_FROM } from "@faire/help-center/views/eventEnum";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import articlePage from "@faire/web-api--source/routes/support/articles/articleId";
import React from "react";
import styled from "styled-components";

import { SectionHeader, SectionWrapper } from "./common";

interface IProps {
  loadTimingRef?: React.RefCallback<HTMLElement>;
}

export const SingleTopic: React.FC<IProps> = ({ loadTimingRef }) => {
  const { strictLocalize } = useStrictLocalization();
  const { aboutFaireSection: guides, isLoading } = useSectionStore();

  return (
    <SectionWrapper $isLoading={isLoading} ref={loadTimingRef}>
      <SectionHeader>
        <StrictLocalMsg
          defaultMessage="About Faire"
          description={{
            text: "Title for a section containing several articles about Faire",
          }}
        />
      </SectionHeader>
      <Spacer height="3x" />
      <InnerWrapper>
        <Section>
          {guides.map((guide) => {
            if (!guide.name) {
              return null;
            }
            return (
              <Tile key={guide.id}>
                <Typography variant="pageHeaderSerifRegular">
                  {guide.name}
                </Typography>
                <Spacer height="1x" />
                <Typography color={Core.text.subdued} maxLines={2}>
                  {guide.preview}
                </Typography>
                <Spacer height="2x" />
                <Button
                  variant="tertiary"
                  linkTo={{
                    pathname: articlePage(`${guide.id}`),
                    state: {
                      autoExpand: true,
                      navigatedFrom: NAVIGATION_FROM.ABOUT_FAIRE,
                      pageFrom: location.pathname,
                      articleTitle: guide.name,
                    },
                  }}
                >
                  {READ_MORE(strictLocalize)}
                </Button>
              </Tile>
            );
          })}
        </Section>
      </InnerWrapper>
    </SectionWrapper>
  );
};

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${responsiveMediaQueries.tabletAndAbove} {
    flex-direction: row;
  }
`;

const Section = styled.div`
  display: flex;
  flex-wrap: wrap;

  ${responsiveMediaQueries.tabletAndAbove} {
    flex: 2;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    &:last-child {
      flex: 1;
    }
  }
`;

const Tile = styled.div`
  width: 100%;
  padding-bottom: 32px;

  ${responsiveMediaQueries.tabletAndAbove} {
    width: 50%;
    padding-left: 40px;
    padding-bottom: 40px;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    width: 33%;
    padding-left: 56px;
  }
`;
